import * as api from '@gitbook/api-client';
import { createEditorDocument } from '@gitbook/doc-core';
import type { Document } from '@gitbook/slate';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useAgentUsage, useRecordAgentUsage } from '../Agent';
import { encodeDocumentForAPI } from '../BasicEditor';
import type { AgentChatMessageSource, AgentChatProps, AgentChatSubmitInput } from './AgentChat';
import { type AgentChatPromptPayload, countAgentChatAttachments } from './AgentChatInput';

/**
 * Send messages as the user, the way the chat does: blocked once the user's message limit is
 * reached, and otherwise counted toward their usage and tracked.
 */
export function usePostAgentMessage(input: {
    submitMessage: (input: AgentChatSubmitInput) => Promise<void> | void;
    currentContext?: api.AIMessageContext;
    isSubmissionBlocked?: boolean;
    onSubmissionBlocked?: AgentChatProps['onSubmissionBlocked'];
    onTrackPostMessage?: AgentChatProps['onTrackPostMessage'];
    /** How many messages the conversation holds, read when one is sent, for tracking. */
    getPreviousMessagesCount: () => number;
    /** What the composer has attached, for tracking. */
    draftAttachments?: Array<{ type: api.AIAttachment['type'] }>;
}): {
    /** Post a chat prompt payload, attributed to where it came from. */
    postPromptPayload: (
        payload: AgentChatPromptPayload,
        messageSource?: AgentChatMessageSource
    ) => void;
    /** Post a suggestion, as its pill does. */
    postSuggestion: (suggestion: string) => void;
} {
    const {
        submitMessage,
        currentContext,
        isSubmissionBlocked = false,
        onSubmissionBlocked,
        onTrackPostMessage,
        getPreviousMessagesCount,
        draftAttachments,
    } = input;
    const agentUsage = useAgentUsage();
    const recordAgentUsage = useRecordAgentUsage();

    const trackPostMessage = useEventCallback(
        (track: { messageSource: AgentChatMessageSource; message: string }) => {
            onTrackPostMessage?.({
                ...track,
                previousMessagesCount: getPreviousMessagesCount(),
                messagesSentLast30Days: agentUsage.total,
                ...countAgentChatAttachments(draftAttachments),
            });
            recordAgentUsage();
        }
    );

    const postPromptPayload = useEventCallback(
        (payload: AgentChatPromptPayload, messageSource: AgentChatMessageSource = 'input') => {
            const contentDocument: Document =
                typeof payload.content === 'string'
                    ? createEditorDocument(payload.content)
                    : payload.content;

            if (isSubmissionBlocked) {
                onSubmissionBlocked?.({
                    messageSource,
                    messageText: contentDocument.text,
                });
                return;
            }

            void submitMessage({
                message: {
                    role: api.AIMessageRole.User,
                    content: encodeDocumentForAPI(contentDocument),
                    attachments: payload.attachments,
                    context: currentContext,
                } satisfies api.AIMessageInput,
                messageSource,
                messageText: contentDocument.text,
            });

            trackPostMessage({
                messageSource,
                message: contentDocument.text,
            });
        }
    );

    const postSuggestion = useEventCallback((suggestion: string) => {
        if (isSubmissionBlocked) {
            onSubmissionBlocked?.({
                messageSource: 'suggestion',
                messageText: suggestion,
            });
            return;
        }

        void submitMessage({
            message: {
                role: api.AIMessageRole.User,
                content: suggestion,
                context: currentContext,
            },
            messageSource: 'suggestion',
            messageText: suggestion,
        });

        trackPostMessage({
            messageSource: 'suggestion',
            message: suggestion,
        });
    });

    return { postPromptPayload, postSuggestion };
}
